import React, { Suspense } from "react";
import UserProvider, { UserContext } from "./contexts/UserContext";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { GoogleMapsProvider } from "./config/GoogleMapsProvider";
// Keep static imports for components used outside routes (shell / always-rendered)
import Loader from "./components/Loader";
import Header from "./components/layouts/Header";
import Footer from "./components/layouts/Footer";
import ContactSupport from "./components/ContactSupport";
import Cookies from "./components/Cookies";
import RouteChangeReset from "./components/RouteChangeReset";
import MobileFooter from "./components/MobileFooter";

// Context providers (static – they wrap the tree)
import { LikesProvider } from "./contexts/LikesCountContext";
import { ReactionProvider } from "./contexts/ReactionContext";
import DeliveryRequestsProvider from "./contexts/DeliveryRequestsContext";
import { StripeProvider } from "./contexts/StripeContext";
import VeriffProvider from "./contexts/VeriffContext";
import NotificationProvider from "./contexts/NotificationContext";

// Utilities (static)
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { ToastContainer } from "react-toastify";

// ─── Route-level lazy imports (code-split per page) ───────────────────────────
const Login = React.lazy(() => import("./components/Login"));
const Signup = React.lazy(() => import("./components/Signup"));
const Welcome = React.lazy(() => import("./components/Welcome"));
const MyVessel = React.lazy(() => import("./components/MyVessel"));
const Congrats = React.lazy(() => import("./components/Congrats"));
const AddVessel = React.lazy(() => import("./components/AddVessel"));
const Portfolio = React.lazy(() => import("./components/Portfolio"));
const DeliveryQuotes = React.lazy(() => import("./components/DeliveryQuotes"));
const QuoteSpecifics = React.lazy(() => import("./components/QuoteSpecifics"));
const DeliveryProcess = React.lazy(() => import("./components/DeliveryProcess"));
const DeliveryRequest = React.lazy(() => import("./components/DeliveryRequest"));
const DeliveryDashboard = React.lazy(() => import("./components/DeliveryDashboard"));
const DeliveryVerification = React.lazy(() => import("./components/DeliveryVerification"));
const ConfirmDeliveryVerification = React.lazy(() => import("./components/ConfirmDeliveryVerification"));
const Profile = React.lazy(() => import("./components/Profile"));
const ContactSupportPage = React.lazy(() => import("./components/ContactSupportPage"));
const StripPaymentSuccess = React.lazy(() => import("./components/StripPayment/StripPaymentSuccess"));
const StripPaymentCancel = React.lazy(() => import("./components/StripPayment/StripPaymentCancel"));
const JobDetailsForm = React.lazy(() => import("./components/JobDetailsForm"));
const ResetPassword = React.lazy(() => import("./components/ResetPassword"));
const ForgotPasswords = React.lazy(() => import("./components/ForgotPasswords"));
const RemaningPayment = React.lazy(() => import("./components/StripPayment/RemaningPayment"));
const AlldeliveryDeatail = React.lazy(() => import("./components/AlldeliveryDeatail"));
const MultiLocationMap = React.lazy(() => import("./components/MultiLocationMap"));
const DeliveryRequestSubmission = React.lazy(() => import("./components/DeliveryRequestSubmission"));
const CrewDasboard = React.lazy(() => import("./components/Crew/CrewDasboard"));
const GalleryPage = React.lazy(() => import("./components/GalleryPage"));
const UserDetails = React.lazy(() => import("./components/UserDetails"));
const CrewJob = React.lazy(() => import("./components/CrewJob"));
const SinglePost = React.lazy(() => import("./Posts/SinglePost"));
const FindFriends = React.lazy(() => import("./Posts/FindFriends"));
const MaintenancePage = React.lazy(() => import("./components/MaintenancePage"));
const Mates = React.lazy(() => import("./components/Mates"));
const MediaLibrary = React.lazy(() => import("./ProfileComponent/MediaLibrary"));
const Ewallet = React.lazy(() => import("./components/StripPayment/Ewallet"));
const Groups = React.lazy(() => import("./components/Groups"));
const PeopleProfile = React.lazy(() => import("./components/PeopleProfile"));
const HashtagPosts = React.lazy(() => import("./Posts/HashTagPost"));
const GroupDashboard = React.lazy(() => import("./components/Group/GroupDashboard"));
const TransferRequests = React.lazy(() => import("./components/TransferRequests"));
const TermsAndConditions = React.lazy(() => import("./Policies/TermsAndConditions"));
const Privacypolicy = React.lazy(() => import("./Policies/Privacypolicy"));
const CookiePolicy = React.lazy(() => import("./Policies/CookiePolicy"));
const ChildPolicy = React.lazy(() => import("./Policies/childpolicy"));
const MobileAuthLoader = React.lazy(() => import("./components/MobileAuthLoader"));
const DeleteAccountPolicy = React.lazy(() => import("./Policies/DeleteAccountPolicy"));

const PrivateRoute = ({ element }) => {
  const { user, isLoading } = React.useContext(UserContext);
  const location = useLocation();

  if (isLoading) {
    return <Loader />;
  }

  const deliveryProtected = location.pathname === "/delivery-process";
  const API_BASE = process.env.REACT_APP_API_URL;

  if (
    deliveryProtected &&
    sessionStorage.getItem("deliveryAccess") !== "granted"
  ) {
    return <Navigate to="/portfolio" replace />;
  }

  return user ? element : <Navigate to="/login" />;
};

const AuthRedirect = ({ element }) => {
  const isLoggedIn = !!localStorage.getItem("token");

  // Allow /login to load when returning from Instagram OAuth connect flow
  const params = new URLSearchParams(window.location.search);
  const hasIgCode = params.get("code") && localStorage.getItem("ig_connect_intent") === "true";

  if (isLoggedIn && !hasIgCode) {
    return <Navigate to="/portfolio" />;
  }

  return element;
};

const Logout = () => {
  const API_BASE = process.env.REACT_APP_API_URL;
  localStorage.removeItem("token");
  localStorage.removeItem("portfolioToggleOption");
  localStorage.removeItem("selectedCurrency");
  localStorage.removeItem("refresh_token");
  sessionStorage.removeItem("cachedUser");
  sessionStorage.removeItem("cachedCurrencies");
  const res = axios.post(`${API_BASE}/logout`).catch(() => {});

  // full reload
  window.location.replace("/login");
  // useNavigate("/login");

  return null; // nothing to render
};

const AppHeader = () => {
  const location = useLocation();
  const hideHeaderRoutes = [
    "/reset-password",
    "/forgot-password",
    "/user-details",
    "/privacy-policy",
    "/terms-and-conditions",
    "/cookie-policy",
    "/auth",
    "/child-protection-policy",
  ];
  if (hideHeaderRoutes.includes(location.pathname)) {
    return null; // Don't render header
  }
  const isActive = (path) => {
    const { pathname, state } = location;
    if (path === "/personal-information") {
      return pathname === path && !state?.userId && !state?.crewprofile_ID
        ? "active"
        : "";
    }
    return pathname === path ? "active" : "";

    // return location.pathname === path ? 'active' : '';
  };
  return <Header isActive={isActive} />;
};

const AppFooter = () => {
  const location = useLocation();
  const hideFooterRoutes = [
    "/reset-password",
    "/forgot-password",
    "/user-details",
    "/privacy-policy",
    "/terms-and-conditions",
    "/cookie-policy",
    "/child-protection-policy",
    "/auth",
  ];

  // Don't render footer on specified routes
  if (hideFooterRoutes.includes(location.pathname)) {
    return null;
  }

  // const isActive = (path) => {
  //   const { pathname, state } = location;
  //   if (path === "/personal-information") {
  //     return pathname === path && !state?.userId && !state?.crewprofile_ID ? "active" : "";
  //   }
  //   return pathname === path ? "active" : "";
  // };

  return (
    <div className="footer_main">
      <MobileFooter />
      {/* <MobileFooter isActive={isActive} /> */}
    </div>
  );
};

const ContactSupportWrapper = () => {
  const { user } = React.useContext(UserContext);
  if (!user || !localStorage.getItem("token")) return null;
  return <ContactSupport />;
};

// Redirect back to crew profile after subscription if pending contact form exists
const PendingContactRedirect = () => {
  const { user } = React.useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (!user || !user.is_premium) return;
    try {
      const saved = sessionStorage.getItem("pendingContactCrewForm");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only redirect if we're on profile/subscription page, not already on the crew profile
        if (parsed.personId && location.pathname === "/profile") {
          navigate("/personal-information", {
            state: { personId: parsed.personId },
            replace: true,
          });
        }
      }
    } catch {}
  }, [user?.is_premium, location.pathname, navigate]);

  return null;
};

function App() {
  // ✅ Decode token and setup auto logout timer
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000; // seconds
        if (decoded.exp < now) {
          Logout();
        } else {
          //setUser(decoded);
          const timeLeft = decoded.exp - now;
          const logoutTimer = setTimeout(Logout, timeLeft * 1000);
          return () => clearTimeout(logoutTimer);
        }
      } catch (err) {
        Logout();
      }
    }
  }, []);

  // ✅ Attach token to Axios & handle 401 globally
  React.useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL;
    // Set initial header
    const initialToken = localStorage.getItem("token");
    axios.defaults.headers.common["Authorization"] = initialToken
      ? `Bearer ${initialToken}`
      : "";

    let isRefreshing = false;
    let subscribers = [];
    const onRefreshed = (token) => subscribers.forEach((cb) => cb(token));
    const addSubscriber = (cb) => subscribers.push(cb);

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const originalRequest = error.config;

        // Only handle 401s here
        if (status !== 401) return Promise.reject(error);

        // Prevent attempting refresh if this request IS the refresh endpoint
        if (originalRequest?.url?.includes("/refresh")) {
          // Refresh endpoint failed — force logout
          Logout();
          return Promise.reject(error);
        }

        // If no refresh token available -> force logout and DO NOT call /refresh
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) {
          if (window.location.pathname !== "/login") Logout();
          return Promise.reject(error);
        }

        // Avoid multiple refresh requests for the same original request
        if (originalRequest._retry) {
          return Promise.reject(error);
        }

        // If a refresh is already in progress, queue this request
        if (isRefreshing) {
          return new Promise((resolve) => {
            addSubscriber((newToken) => {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              resolve(axios(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const res = await axios.post(`${API_BASE}/refresh`, {
            refresh_token: refreshToken,
          });
          const newAccessToken = res.data.token || res.data.access_token;
          const newRefreshToken = res.data.refresh_token;
          if (!newAccessToken)
            throw new Error("Refresh did not return access token");

          // Store and update defaults
          localStorage.setItem("token", newAccessToken);
          if (newRefreshToken)
            localStorage.setItem("refresh_token", newRefreshToken);
          axios.defaults.headers.common["Authorization"] =
            `Bearer ${newAccessToken}`;

          // Resume queued requests
          onRefreshed(newAccessToken);
          subscribers = [];

          // Retry original request
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return axios(originalRequest);
        } catch (err) {
          // Refresh failed -> logout
          Logout();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [Logout]);
  return (
    <UserProvider>
      <NotificationProvider>
      <VeriffProvider>
        <LikesProvider>
          <GoogleMapsProvider>
              <ReactionProvider>
                <StripeProvider>
                <DeliveryRequestsProvider>
                  <Router>
                    <ToastContainer
                      position="top-right"
                      autoClose={3000}
                      hideProgressBar={false}
                      newestOnTop
                      closeOnClick
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                    />
                    <RouteChangeReset />
                    <AppHeader />
                    <Suspense fallback={null}>
                    <Routes>
                      <Route
                        exact
                        path="/privacy-policy"
                        element={<Privacypolicy />}
                      />
                      <Route
                        exact
                        path="/terms-and-conditions"
                        element={<TermsAndConditions />}
                      />
                      <Route
                        exact
                        path="/cookie-policy"
                        element={<CookiePolicy />}
                      />
                      <Route
                        exact
                        path="/child-protection-policy"
                        element={<ChildPolicy />}
                      />
                      {/* <Route exact path="/signup" element={<Signup />} /> */}
                      <Route
                        exact
                        path="/signup"
                        element={<AuthRedirect element={<Signup />} />}
                      />
                      <Route
                        exact
                        path="/welcome"
                        element={<Welcome />}
                      />
                      <Route
                        exact
                        path="/auth"
                        element={<MobileAuthLoader />}
                      />
                      <Route exact path="/logout" element={<Logout />} />
                      <Route
                        path="/"
                        element={<Navigate to="/login" replace />}
                      />
                      <Route
                        exact
                        path="/contact-support"
                        element={<ContactSupportPage />}
                      />
                      <Route
                        exact
                        path="/account-deletion-guide"
                        element={<DeleteAccountPolicy />}
                      />

                      {/* <Route exact path="/" element={<AuthRedirect element={<Login />} />} /> */}

                      {/* <Route exact path="/terms-and-conditions" element={<AuthRedirect element={<TermsAndConditions />} />} />
            <Route exact path="/privacy-policy" element={<AuthRedirect element={<Privacypolicy />} />} />
            <Route exact path="/cookie-policy" element={<AuthRedirect element={<CookiePolicy />} />} /> */}
                      <Route
                        exact
                        path="/login"
                        element={<AuthRedirect element={<Login />} />}
                      />
                      <Route
                        exact
                        path="/reset-password"
                        element={<AuthRedirect element={<ResetPassword />} />}
                      />
                      <Route
                        exact
                        path="/forgot-password"
                        element={<AuthRedirect element={<ForgotPasswords />} />}
                      />
                      <Route
                        exact
                        path="/user-details"
                        element={<AuthRedirect element={<UserDetails />} />}
                      />

                      <Route
                        exact
                        path="/my-vessel"
                        element={<PrivateRoute element={<MyVessel />} />}
                      />
                      <Route
                        exact
                        path="/add-vessel"
                        element={<PrivateRoute element={<AddVessel />} />}
                      />
                      <Route
                        exact
                        path="/portfolio"
                        element={<PrivateRoute element={<Portfolio />} />}
                      />
                      <Route
                        exact
                        path="/profile"
                        element={<PrivateRoute element={<Profile />} />}
                      />
                      <Route
                        exact
                        path="/payment-success/:encodedId"
                        element={
                          <PrivateRoute element={<StripPaymentSuccess />} />
                        }
                      />
                      <Route
                        exact
                        path="/payment-cancel"
                        element={
                          <PrivateRoute element={<StripPaymentCancel />} />
                        }
                      />
                      <Route
                        exact
                        path="/delivery"
                        element={<PrivateRoute element={<DeliveryRequest />} />}
                      />
                      <Route
                        exact
                        path="/delivery-completed"
                        element={<PrivateRoute element={<Congrats />} />}
                      />
                      <Route
                        exact
                        path="/personal-information"
                        element={<PrivateRoute element={<QuoteSpecifics />} />}
                      />
                      <Route
                        exact
                        path="/delivery-quotes"
                        element={<PrivateRoute element={<DeliveryQuotes />} />}
                      />
                      <Route
                        exact
                        path="/delivery-process"
                        element={<PrivateRoute element={<DeliveryProcess />} />}
                      />
                      <Route
                        exact
                        path="/delivery-dashboard/:encodedId"
                        element={
                          <PrivateRoute element={<DeliveryDashboard />} />
                        }
                      />
                      <Route
                        exact
                        path="/delivery-verification-check-in/:encodedId"
                        element={
                          <PrivateRoute element={<DeliveryVerification />} />
                        }
                      />
                      <Route
                        exact
                        path="/delivery-process-pay-remaining/:encodedId"
                        element={<PrivateRoute element={<RemaningPayment />} />}
                      />
                      <Route
                        exact
                        path="/delivery-verification-check-out/:encodedId"
                        element={
                          <PrivateRoute
                            element={<ConfirmDeliveryVerification />}
                          />
                        }
                      />
                      <Route
                        exact
                        path="/job-details-form"
                        element={<PrivateRoute element={<JobDetailsForm />} />}
                      />
                      <Route
                        exact
                        path="/all-delivery-details"
                        element={
                          <PrivateRoute element={<AlldeliveryDeatail />} />
                        }
                      />
                      {/* <Route exact path="/map-picker" element={<PrivateRoute element={<MapPickerModal />} />} />
            <Route exact path="/miles-calculator" element={<PrivateRoute element={<MilesCalculator />} />} /> */}
                      <Route
                        exact
                        path="/location-picker"
                        element={
                          <PrivateRoute element={<MultiLocationMap />} />
                        }
                      />
                      <Route
                        exact
                        path="/delivery-request-submission"
                        element={
                          <PrivateRoute
                            element={<DeliveryRequestSubmission />}
                          />
                        }
                      />
                      <Route
                        exact
                        path="/crew"
                        element={<PrivateRoute element={<CrewDasboard />} />}
                      />
                      <Route
                        exact
                        path="/crew-job"
                        element={<PrivateRoute element={<CrewJob />} />}
                      />
                      <Route
                        exact
                        path="/gallery"
                        element={<PrivateRoute element={<GalleryPage />} />}
                      />
                      <Route
                        exact
                        path="/find-friends"
                        element={<PrivateRoute element={<FindFriends />} />}
                      />
                      <Route
                        exact
                        path="/maintenance"
                        element={<PrivateRoute element={<MaintenancePage />} />}
                      />
                      <Route
                        exact
                        path="/media-library"
                        element={<PrivateRoute element={<MediaLibrary />} />}
                      />
                      <Route
                        exact
                        path="/wallet"
                        element={<PrivateRoute element={<Ewallet />} />}
                      />
                      <Route
                        exact
                        path="/groups/group-dashboard/:groupNameOrId"
                        element={<PrivateRoute element={<GroupDashboard />} />}
                      />
                      <Route
                        exact
                        path="/transfer-requests"
                        element={
                          <PrivateRoute element={<TransferRequests />} />
                        }
                      />
                      {/* Single post route */}
                      <Route
                        exact
                        path="/post/:id"
                        element={<PrivateRoute element={<SinglePost />} />}
                      />
                      {/* Hashtag posts route */}
                      <Route
                        exact
                        path="/hashtag/:tagId"
                        element={<PrivateRoute element={<HashtagPosts />} />}
                      />
                      <Route
                        exact
                        path="/profiles"
                        element={<PrivateRoute element={<PeopleProfile />} />}
                      />
                      {/* Mates route */}
                      <Route
                        exact
                        path="/mates"
                        element={<PrivateRoute element={<Mates />} />}
                      />
                      {/* Groups route */}
                      <Route
                        exact
                        path="/groups"
                        element={<PrivateRoute element={<Groups />} />}
                      />

                      {/* Add this catch-all route at the end */}
                      <Route
                        path="*"
                        element={
                          localStorage.getItem("token") ? (
                            <Navigate to="/portfolio" replace />
                          ) : (
                            <Navigate to="/login" replace />
                          )
                        }
                      />
                    </Routes>
                    </Suspense>
                    {/* Show ContactSupport only when NOT on login/signup pages */}
                    <ContactSupportWrapper />
                    <PendingContactRedirect />
                    {/* {window.location.pathname === "/login" && <ContactSupport />} */}
                    {/* {localStorage.getItem("token") && <Cookies />} */}
                    {["/login", "/signup"].includes(
                      window.location.pathname,
                    ) && <Footer />}
                    {["/login", "/signup"].includes(
                      window.location.pathname,
                    ) && <Cookies />}
                    <AppFooter />
                  </Router>
                </DeliveryRequestsProvider>
                </StripeProvider>
              </ReactionProvider>
          </GoogleMapsProvider>
        </LikesProvider>
      </VeriffProvider>
      </NotificationProvider>
    </UserProvider>
  );
}

export default App;

